import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  ShieldCheck,
  User,
  Copy,
  ExternalLink,
  MessageCircle,
  AlertCircle,
  Trash2,
  CheckCircle2,
  Tablet,
  Smartphone,
  FileText,
  Sparkles,
  Info,
  Camera,
} from 'lucide-react';
import { episService } from '../../../services/epis.service';
import { colaboradoresService } from '../../../services/colaboradores.service';
import { EpiCameraCapture } from './EpiCameraCapture';
import { EpiSignatureCanvas } from './EpiSignatureCanvas';
import SearchableSelect from '../../../components/SearchableSelect';
import { formatCPF } from '../../../utils/formatters';
import type {
  EpiEntregaContract,
  EpiEstoqueSaldoContract,
  EpiMatrizContract,
  EpiMatrizItemContract,
  CriarEntregaEpiPayload,
} from '@sstexames/shared-types';

interface ColaboradorOption {
  id: number;
  nome: string;
  cpf: string;
  cargo_id?: number | null;
  cargo_nome?: string;
  setor_id?: number | null;
  setor_nome?: string;
}

interface ItemEntregaForm {
  epi_id: number;
  descricao_epi: string;
  ca_numero?: string;
  quantidade: number;
  tamanho?: string;
  lote?: string;
  regra_origem_tipo: 'REGULATORIO' | 'CUSTOMIZADO_EMPRESA' | 'CUSTOMIZADO_GRUPO' | 'AVULSO_MANUAL';
  matriz_item_id?: number;
}

interface EntregaEpiModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  empresaId: number;
  initialColaboradorId?: number;
}

export const EntregaEpiModal: React.FC<EntregaEpiModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  empresaId,
  initialColaboradorId,
}) => {
  const [loading, setLoading] = useState(false);
  const [colaboradores, setColaboradores] = useState<ColaboradorOption[]>([]);
  const [selectedColaboradorId, setSelectedColaboradorId] = useState<number | ''>(initialColaboradorId || '');
  const [estoqueSaldos, setEstoqueSaldos] = useState<EpiEstoqueSaldoContract[]>([]);
  const [matriz, setMatriz] = useState<EpiMatrizContract | null>(null);

  // Form states - método inicia como null para só exibir opções após a escolha
  const [itensEntrega, setItensEntrega] = useState<ItemEntregaForm[]>([]);
  const [metodoAssinatura, setMetodoAssinatura] = useState<'presencial_portal' | 'link_celular' | null>(null);
  const [fotoRhBase64, setFotoRhBase64] = useState<string>('');
  const [fotoColaboradorBase64, setFotoColaboradorBase64] = useState<string>('');
  const [assinaturaBase64, setAssinaturaBase64] = useState<string>('');
  const [observacoes, setObservacoes] = useState('');

  // Post-creation link modal state
  const [entregaGerada, setEntregaGerada] = useState<EpiEntregaContract | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Selected colaborador object
  const selectedColaborador = useMemo(() => {
    return colaboradores.find((c) => c.id === Number(selectedColaboradorId)) || null;
  }, [colaboradores, selectedColaboradorId]);

  // Lista formatada de EPIs para o termo legal
  const listaEpisFormatada = useMemo(() => {
    if (itensEntrega.length === 0) return '';
    return itensEntrega
      .map((i) => `${i.descricao_epi} (${i.quantidade} un${i.ca_numero ? `, CA: ${i.ca_numero}` : ''})`)
      .join(', ');
  }, [itensEntrega]);

  // Load initial data
  useEffect(() => {
    if (!isOpen || !empresaId) return;

    const fetchDados = async () => {
      try {
        setLoading(true);
        const [colabsRes, estoqueRes, matrizRes] = await Promise.all([
          colaboradoresService.findAll({ limit: 200 }),
          episService.getEstoqueSaldos(empresaId),
          episService.getMatriz(empresaId),
        ]);

        const colabList: ColaboradorOption[] = (colabsRes?.data || []).map((c) => ({
          id: c.id,
          nome: c.nome,
          cpf: c.cpf,
          cargo_id: c.cargo?.id,
          cargo_nome: c.cargo?.nome || 'Sem Cargo',
          setor_id: c.setor?.id,
          setor_nome: c.setor?.nome || 'Sem Setor',
        }));

        setColaboradores(colabList);
        setEstoqueSaldos(estoqueRes || []);
        setMatriz(matrizRes || null);
      } catch (err) {
        console.error('Erro ao carregar dados do modal de entrega:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDados();
  }, [isOpen, empresaId]);

  // Auto-fill recommended EPIs when colaborador is selected
  useEffect(() => {
    if (!selectedColaborador || !matriz?.versao_atual?.itens) {
      return;
    }

    const cargoId = selectedColaborador.cargo_id;
    const setorId = selectedColaborador.setor_id;

    const itensCargo: EpiMatrizItemContract[] = matriz.versao_atual.itens.filter((item) => {
      if (item.cargo_id === cargoId) {
        if (!item.setor_id || item.setor_id === setorId) return true;
      }
      return false;
    });

    if (itensCargo.length > 0) {
      setItensEntrega(
        itensCargo.map((item) => ({
          epi_id: item.epi_id,
          descricao_epi: item.epi_descricao || item.epi_nome || 'EPI',
          ca_numero: item.ca_numero || undefined,
          quantidade: item.quantidade || 1,
          regra_origem_tipo: item.regra_origem_tipo || item.origem || 'REGULATORIO',
          matriz_item_id: item.id,
        })),
      );
    } else {
      setItensEntrega([]);
    }
  }, [selectedColaborador, matriz]);

  const handleAddItem = (epiId: number) => {
    const epiEstoque = estoqueSaldos.find((e) => e.epi_id === epiId);
    if (!epiEstoque) return;

    setItensEntrega((prev) => [
      ...prev,
      {
        epi_id: epiEstoque.epi_id,
        descricao_epi: epiEstoque.epi_descricao,
        ca_numero: epiEstoque.ca_numero || undefined,
        quantidade: 1,
        regra_origem_tipo: 'AVULSO_MANUAL',
      },
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setItensEntrega((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateItem = (index: number, field: keyof ItemEntregaForm, value: string | number) => {
    setItensEntrega((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!selectedColaborador) {
      setErrorMessage('Selecione um colaborador para a entrega.');
      return;
    }
    if (itensEntrega.length === 0) {
      setErrorMessage('Adicione pelo menos um EPI à entrega.');
      return;
    }
    if (!metodoAssinatura) {
      setErrorMessage('Selecione o método de efetivação/assinatura (Presencial ou Link Remoto).');
      return;
    }
    if (!fotoRhBase64) {
      setErrorMessage('A foto de evidência da entrega (Responsável RH/SST) é obrigatória.');
      return;
    }

    if (metodoAssinatura === 'presencial_portal') {
      if (!fotoColaboradorBase64) {
        setErrorMessage('A foto biométrica facial do colaborador é obrigatória para entrega presencial.');
        return;
      }
      if (!assinaturaBase64) {
        setErrorMessage('A assinatura digital do colaborador no quadro é obrigatória para entrega presencial.');
        return;
      }
    }

    try {
      setLoading(true);

      const payload: CriarEntregaEpiPayload = {
        empresa_id: empresaId,
        colaborador_id: selectedColaborador.id,
        cargo_id: selectedColaborador.cargo_id || 0,
        setor_id: selectedColaborador.setor_id || 0,
        metodo_assinatura: metodoAssinatura,
        foto_rh_base64: fotoRhBase64,
        observacoes: observacoes || undefined,
        itens: itensEntrega.map((item) => ({
          epi_id: item.epi_id,
          quantidade: Number(item.quantidade),
          tamanho: item.tamanho || undefined,
          lote: item.lote || undefined,
          ca_numero: item.ca_numero || undefined,
          regra_origem_tipo: item.regra_origem_tipo,
          matriz_item_id: item.matriz_item_id,
        })),
      };

      const entrega = await episService.criarEntrega(payload);

      // Se for presencial, assinar imediatamente no mesmo fluxo
      if (metodoAssinatura === 'presencial_portal') {
        await episService.assinarEntrega(entrega.id, {
          foto_colaborador_base64: fotoColaboradorBase64,
          assinatura_base64: assinaturaBase64,
        });
        onSuccess();
        onClose();
      } else {
        setEntregaGerada(entrega);
        onSuccess();
      }
    } catch (err: unknown) {
      console.error('Erro ao emitir entrega:', err);
      const errorMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao processar entrega de EPI.';
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-3xl max-w-4xl w-full shadow-2xl border border-gray-100 overflow-hidden animate-in zoom-in-95 duration-200 my-auto">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-gray-50/80 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100 shadow-2xs">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-gray-900 tracking-tight">Nova Ficha de Entrega de EPI (NR-06)</h3>
                <span className="bg-emerald-100 text-emerald-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full border border-emerald-200">
                  Digital & Legal
                </span>
              </div>
              <p className="text-xs text-gray-500">Emissão, conferência física, biometria facial e assinatura eletrônica</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        {entregaGerada ? (
          /* Tela de Sucesso com Link de Assinatura Celular */
          <div className="p-8 sm:p-10 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-3xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4 border border-emerald-100 shadow-sm">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <h4 className="text-lg font-bold text-gray-900 mb-1">Ficha de Entrega Gerada com Sucesso!</h4>
            <p className="text-sm text-gray-600 max-w-lg mb-6 leading-relaxed">
              Envie o link seguro para o colaborador <strong>{entregaGerada.colaborador_nome}</strong> para que ele faça a conferência dos equipamentos, biometria facial e assinatura diretamente pelo celular.
            </p>

            <div className="w-full max-w-lg bg-gray-50/80 p-5 rounded-2xl border border-gray-200 mb-6 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-mono text-gray-700 truncate select-all bg-white px-3.5 py-2.5 rounded-xl border border-gray-200 flex-1 text-left shadow-2xs">
                  {entregaGerada.link_assinatura_publico || `${window.location.origin}/epi/aceite/${entregaGerada.token_assinatura}`}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    copyToClipboard(
                      entregaGerada.link_assinatura_publico ||
                        `${window.location.origin}/epi/aceite/${entregaGerada.token_assinatura}`,
                    )
                  }
                  className="px-3.5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors shadow-xs shrink-0"
                >
                  <Copy className="w-3.5 h-3.5" /> {copiedLink ? 'Copiado!' : 'Copiar'}
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                <a
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                    `Olá ${entregaGerada.colaborador_nome}, segue o link para conferência e assinatura da sua ficha de entrega de EPIs: ${
                      entregaGerada.link_assinatura_publico ||
                      `${window.location.origin}/epi/aceite/${entregaGerada.token_assinatura}`
                    }`,
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors shadow-xs"
                >
                  <MessageCircle className="w-4 h-4" /> Enviar por WhatsApp
                </a>

                <a
                  href={entregaGerada.link_assinatura_publico || `/epi/aceite/${entregaGerada.token_assinatura}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2.5 bg-white border border-gray-300 hover:bg-gray-100 text-gray-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors shadow-2xs"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Abrir no Navegador
                </a>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="px-8 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold hover:bg-black transition-colors shadow-sm"
            >
              Concluir e Voltar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[82vh] overflow-y-auto">
            {errorMessage && (
              <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-2xl text-xs flex items-center gap-2.5 shadow-2xs">
                <AlertCircle className="w-4 h-4 flex-shrink-0 text-rose-600" />
                <span className="font-medium">{errorMessage}</span>
              </div>
            )}

            {/* ========================================================= */}
            {/* ETAPA 1: SELEÇÃO DO COLABORADOR                           */}
            {/* ========================================================= */}
            <div className="space-y-2">
              <SearchableSelect
                label="Colaborador Destinatário"
                required
                options={colaboradores.map((colab) => ({
                  id: colab.id,
                  label: `${colab.nome} - ${colab.cargo_nome || 'Sem cargo'} (${formatCPF(colab.cpf)})`,
                }))}
                value={selectedColaboradorId || null}
                onChange={(val) => setSelectedColaboradorId(val ? Number(val) : '')}
                placeholder="Selecione o colaborador que receberá os EPIs..."
                searchPlaceholder="Buscar por nome, cargo ou CPF..."
              />

              {selectedColaborador && (
                <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl flex flex-wrap items-center justify-between gap-3 text-xs shadow-2xs">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-emerald-700 font-bold">
                      <User className="w-4 h-4" />
                    </div>
                    <div>
                      <div className="font-bold text-slate-900">{selectedColaborador.nome}</div>
                      <div className="text-[11px] text-slate-500">
                        Cargo: <strong className="text-slate-700">{selectedColaborador.cargo_nome}</strong> • Setor:{' '}
                        <strong className="text-slate-700">{selectedColaborador.setor_nome}</strong>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white px-3 py-1 rounded-xl border border-slate-200 font-mono text-[11px] font-bold text-slate-700">
                    CPF: {formatCPF(selectedColaborador.cpf)}
                  </div>
                </div>
              )}
            </div>

            {/* ========================================================= */}
            {/* ETAPA 2: EQUIPAMENTOS ENTREGUES (EPIS)                    */}
            {/* ========================================================= */}
            <div className="space-y-2.5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs font-bold text-gray-800 tracking-tight">
                    Equipamentos Entregues (EPIs) <span className="text-rose-500">*</span>
                  </label>
                  {itensEntrega.length > 0 && (
                    <span className="text-[10px] font-extrabold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                      {itensEntrega.length} item(ns)
                    </span>
                  )}
                </div>
                <div className="w-full sm:w-72">
                  <SearchableSelect
                    options={estoqueSaldos.map((est) => ({
                      id: est.epi_id,
                      label: `${est.epi_descricao} (Saldo: ${est.saldo_disponivel})`,
                    }))}
                    value=""
                    onChange={(val) => {
                      if (val) {
                        handleAddItem(Number(val));
                      }
                    }}
                    placeholder="+ Adicionar outro EPI..."
                    searchPlaceholder="Buscar EPI no estoque..."
                  />
                </div>
              </div>

              {itensEntrega.length === 0 ? (
                <div className="p-6 border-2 border-dashed border-gray-200 rounded-2xl text-center text-xs text-gray-400 bg-gray-50/50">
                  Nenhum EPI associado. Selecione um colaborador para carregar a matriz recomendada ou adicione manualmente acima.
                </div>
              ) : (
                <div className="space-y-2 border border-gray-200 rounded-2xl p-3 bg-gray-50/50 max-h-56 overflow-y-auto">
                  {itensEntrega.map((item, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-white rounded-xl border border-gray-200/90 shadow-2xs flex flex-wrap items-center justify-between gap-3 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex-1 min-w-[200px]">
                        <span className="text-xs font-bold text-gray-900 block leading-tight">{item.descricao_epi}</span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5 font-bold">
                            {item.regra_origem_tipo}
                          </span>
                          {item.ca_numero && (
                            <span className="text-[10px] text-gray-500 font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                              CA: {item.ca_numero}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div>
                          <label className="text-[10px] font-bold text-gray-500 block mb-0.5">Qtd</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantidade}
                            onChange={(e) => handleUpdateItem(idx, 'quantidade', Number(e.target.value))}
                            className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-lg text-center font-bold text-gray-800 focus:ring-1 focus:ring-emerald-500"
                          />
                        </div>

                        <div>
                          <label className="text-[10px] font-bold text-gray-500 block mb-0.5">Tam.</label>
                          <input
                            type="text"
                            placeholder="G, 41"
                            value={item.tamanho || ''}
                            onChange={(e) => handleUpdateItem(idx, 'tamanho', e.target.value)}
                            className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-lg text-center text-gray-800 focus:ring-1 focus:ring-emerald-500"
                          />
                        </div>

                        <div>
                          <label className="text-[10px] font-bold text-gray-500 block mb-0.5">Lote</label>
                          <input
                            type="text"
                            placeholder="Lote"
                            value={item.lote || ''}
                            onChange={(e) => handleUpdateItem(idx, 'lote', e.target.value)}
                            className="w-20 px-2 py-1 text-xs border border-gray-300 rounded-lg text-gray-800 focus:ring-1 focus:ring-emerald-500"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveItem(idx)}
                          title="Remover EPI"
                          className="mt-3.5 p-1.5 text-gray-400 hover:text-rose-600 rounded-lg hover:bg-rose-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ========================================================= */}
            {/* ETAPA 3: MÉTODO DE EFETIVAÇÃO (LADO A LADO)                */}
            {/* ========================================================= */}
            <div className="border-t border-gray-100 pt-5 space-y-3">
              <label className="block text-xs font-bold text-gray-800 tracking-tight">
                Método de Efetivação / Assinatura <span className="text-rose-500">*</span>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {/* Opção 1: Presencial no Portal */}
                <button
                  type="button"
                  onClick={() => setMetodoAssinatura('presencial_portal')}
                  className={`p-4 rounded-2xl border-2 text-left transition-all relative overflow-hidden group ${
                    metodoAssinatura === 'presencial_portal'
                      ? 'border-emerald-500 bg-emerald-50/40 text-emerald-950 shadow-sm ring-2 ring-emerald-500/20'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 text-gray-700'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                        metodoAssinatura === 'presencial_portal'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'bg-gray-100 text-gray-600 group-hover:bg-emerald-100 group-hover:text-emerald-700'
                      }`}
                    >
                      <Tablet className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <span className="text-xs font-bold block">Agora (Presencial no Portal)</span>
                        <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                          Imediato
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 leading-tight">
                        Evidência fotográfica, biometria facial e assinatura do colaborador colhidas agora neste terminal/tablet.
                      </p>
                    </div>
                  </div>
                </button>

                {/* Opção 2: Link Remoto / Celular */}
                <button
                  type="button"
                  onClick={() => setMetodoAssinatura('link_celular')}
                  className={`p-4 rounded-2xl border-2 text-left transition-all relative overflow-hidden group ${
                    metodoAssinatura === 'link_celular'
                      ? 'border-emerald-500 bg-emerald-50/40 text-emerald-950 shadow-sm ring-2 ring-emerald-500/20'
                      : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/80 text-gray-700'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                        metodoAssinatura === 'link_celular'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'bg-gray-100 text-gray-600 group-hover:bg-emerald-100 group-hover:text-emerald-700'
                      }`}
                    >
                      <Smartphone className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <span className="text-xs font-bold block">Link Remoto / Celular</span>
                        <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-800 border border-slate-200">
                          Sem Papel
                        </span>
                      </div>
                      <p className="text-[11px] text-gray-500 leading-tight">
                        Gera link para WhatsApp/QR Code com biometria facial e conferência na tela do smartphone do colaborador.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* ========================================================= */}
            {/* ETAPA 4: DETALHES CONDICIONAIS CONFORME MÉTODO ESCOLHIDO  */}
            {/* ========================================================= */}
            {metodoAssinatura === null ? (
              // Mensagem instrutiva amigável antes da escolha
              <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center flex flex-col items-center justify-center gap-2 text-slate-500">
                <Info className="w-5 h-5 text-slate-400" />
                <p className="text-xs font-medium">
                  Selecione acima se a formalização será <strong>Presencial no Portal</strong> ou via <strong>Link no Celular</strong> para liberar as etapas de evidências e assinatura.
                </p>
              </div>
            ) : metodoAssinatura === 'link_celular' ? (
              // FLUXO REMOTO / CELULAR
              <div className="border-t border-gray-100 pt-5 space-y-4 animate-in fade-in duration-200">
                {/* Informativo do Fluxo Remoto */}
                <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-200 flex items-start gap-3 text-xs text-emerald-950">
                  <Sparkles className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="font-bold">Assinatura Digital Remota via Celular do Colaborador</p>
                    <p className="text-emerald-800 text-[11px] leading-relaxed">
                      Ao concluir, será gerado um link seguro (com envio por WhatsApp ou QR Code). O colaborador acessará a ficha pelo próprio aparelho, conferirá os EPIs recebidos, capturará sua biometria facial frontal e assinará na tela touch.
                    </p>
                  </div>
                </div>

                {/* Evidência da Entrega (RH / SST) */}
                <div className="pt-1">
                  <EpiCameraCapture
                    label="Foto de Evidência da Entrega (Responsável RH/SST)"
                    required
                    modeType="evidencia"
                    facingMode="environment"
                    onPhotoCaptured={(base64) => setFotoRhBase64(base64)}
                    initialPhotoUrl={fotoRhBase64 || null}
                  />
                </div>
              </div>
            ) : (
              // FLUXO PRESENCIAL NO PORTAL (AGORA)
              <div className="border-t border-gray-100 pt-5 space-y-5 animate-in fade-in duration-200">
                {/* 1. FOTOS LADO A LADO (RH e COLABORADOR) */}
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Camera className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-bold text-gray-800 tracking-tight">
                      Evidências Fotográficas e Biometria Facial
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Foto Evidência RH/SST */}
                    <EpiCameraCapture
                      label="Evidência da Entrega (RH/SST)"
                      required
                      modeType="evidencia"
                      facingMode="environment"
                      onPhotoCaptured={(base64) => setFotoRhBase64(base64)}
                      initialPhotoUrl={fotoRhBase64 || null}
                    />

                    {/* Foto Biometria do Colaborador */}
                    <EpiCameraCapture
                      label="Biometria Facial do Colaborador"
                      required
                      modeType="biometria"
                      facingMode="user"
                      onPhotoCaptured={(base64) => setFotoColaboradorBase64(base64)}
                      initialPhotoUrl={fotoColaboradorBase64 || null}
                    />
                  </div>
                </div>

                {/* 2. TERMO LEGAL DE DECLARAÇÃO DE RECEBIMENTO (NR-06) */}
                <div className="bg-slate-50 border border-slate-200/90 rounded-2xl p-4 text-xs text-slate-800 space-y-2.5 shadow-2xs">
                  <div className="flex items-center justify-between border-b border-slate-200/70 pb-2">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-800">
                        <FileText className="w-4 h-4" />
                      </div>
                      <span className="font-bold text-slate-900 tracking-tight text-xs uppercase">
                        Declaração de Recebimento e Responsabilidade (NR-06)
                      </span>
                    </div>
                    <span className="text-[10px] font-bold text-slate-600 bg-white px-2.5 py-0.5 rounded-full border border-slate-200">
                      NR-06 • Portaria MTP 4.219
                    </span>
                  </div>

                  <div className="bg-white p-3.5 rounded-xl border border-slate-200/90 text-[11px] leading-relaxed text-slate-700 font-normal">
                    "Eu, <strong>{selectedColaborador?.nome || '____________________'}</strong>, portador(a) do CPF nº{' '}
                    <strong>{selectedColaborador?.cpf ? formatCPF(selectedColaborador.cpf) : '___.___.___-__'}</strong>, na função de{' '}
                    <strong>{selectedColaborador?.cargo_nome || 'colaborador(a)'}</strong>, declaro ter recebido da empresa o(s) Equipamento(s) de Proteção Individual (EPI) listado(s) acima (<strong>{listaEpisFormatada || 'EPIs descritos nesta ficha'}</strong>), no dia{' '}
                    <strong>{new Date().toLocaleDateString('pt-BR')}</strong>, novos e em perfeitas condições de uso, conservação e funcionamento, acompanhados de seus respectivos Certificados de Aprovação (CA) válidos.<br /><br />
                    Declaro estar ciente da obrigatoriedade do uso destes equipamentos durante toda a jornada de trabalho, comprometendo-me a zelar pela sua guarda e conservação, bem como comunicar imediatamente ao setor de SST/RH qualquer alteração ou dano que os torne impróprios para uso, nos termos da <strong>NR-06 e NR-01</strong>."
                  </div>

                  <div className="flex items-center justify-between text-[10px] text-slate-500 font-medium px-1">
                    <span>Data da Formalização: <strong>{new Date().toLocaleDateString('pt-BR')}</strong></span>
                    <span>Validação por biometria e assinatura eletrônica (MP 2.200-2/2001)</span>
                  </div>
                </div>

                {/* 3. ASSINATURA DIGITAL DO COLABORADOR */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-800 tracking-tight flex items-center gap-1">
                      <span>Assinatura Digital do Colaborador</span>
                      <span className="text-rose-500 font-bold">*</span>
                    </span>
                    <span className="text-[11px] text-gray-500">Rubrique ou assine com o mouse ou toque</span>
                  </div>
                  <EpiSignatureCanvas
                    onSave={(base64) => setAssinaturaBase64(base64)}
                    onClear={() => setAssinaturaBase64('')}
                  />
                </div>
              </div>
            )}

            {/* ========================================================= */}
            {/* OBSERVAÇÕES ADICIONAIS                                    */}
            {/* ========================================================= */}
            {metodoAssinatura !== null && (
              <div className="space-y-1.5 border-t border-gray-100 pt-4">
                <label className="block text-xs font-bold text-gray-700">Observações Adicionais (Opcional)</label>
                <textarea
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                  rows={2}
                  placeholder="Ex: Treinamento prático de ajuste, guarda e higienização ministrado no ato da entrega."
                  className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 text-gray-800"
                />
              </div>
            )}

            {/* ========================================================= */}
            {/* FOOTER ACTIONS                                            */}
            {/* ========================================================= */}
            <div className="border-t border-gray-100 pt-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2.5 text-xs font-bold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-2xs"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading || metodoAssinatura === null}
                className="px-6 py-2.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading
                  ? 'Processando...'
                  : metodoAssinatura === 'presencial_portal'
                  ? 'Efetivar Entrega Presencial'
                  : metodoAssinatura === 'link_celular'
                  ? 'Gerar Ficha e Enviar Link'
                  : 'Selecione o Método Acima'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
